import { queryOptions, useQuery } from '@tanstack/react-query';
import { ApiError } from '../../components/ApiLoadingGuard/ApiLoadingGuard';
import { EdgeEstimationEntry, EdgeSizeChipData } from '../../components/Prediction/types';
import { MipEdgeSizesPredictionDTO, TruckRecordingMipService } from '../generated';
import { EdgeEstimationConfig, transformEdgeEstimation } from './edgeEstimation';

export interface TruckRecordingMipEdgeEstimationOptionsProps {
  jobsiteId?: string;
  mipReportId?: string;
  config?: EdgeEstimationConfig;
  enabled?: boolean;
}

export function truckRecordingMipEdgeEstimationOptions({
  jobsiteId,
  mipReportId,
  enabled = true,
  config,
}: TruckRecordingMipEdgeEstimationOptionsProps) {
  return queryOptions<EdgeSizeChipData[], ApiError>({
    queryKey: ['truckRecordingMipEdgeEstimation', jobsiteId, mipReportId, config],
    queryFn: async () => {
      if (!jobsiteId || !mipReportId) {
        throw new Error('jobsiteId and mipReportId are required');
      }

      const response = await TruckRecordingMipService.truckRecordingMipControllerGetEdgeSizes({
        path: { jobsiteId, mipReportId },
      });

      if (response.data !== undefined) {
        return transformEdgeEstimation(toEdgeEstimationEntries(response.data), config);
      }

      throw (response as any).error || new Error(response.response?.statusText || 'Unknown error');
    },
    enabled: !!jobsiteId && !!mipReportId && enabled,
  });
}

export function useTruckRecordingMipEdgeEstimation(props: TruckRecordingMipEdgeEstimationOptionsProps) {
  return useQuery(truckRecordingMipEdgeEstimationOptions(props));
}

function toEdgeEstimationEntries(resp: MipEdgeSizesPredictionDTO): EdgeEstimationEntry[] {
  // NOTE: currently in mip width and height are flipped. Change once that has been fixed.
  return (resp?.data?.objects ?? []).flatMap((obj): EdgeEstimationEntry[] => {
    if (obj.width === undefined || obj.height === undefined) return [];
    const { boundingBox: bb } = obj;
    if (bb?.xMin !== undefined && bb?.yMin !== undefined && bb?.xMax !== undefined && bb?.yMax !== undefined) {
      return [[obj.height, obj.width, [bb.xMin, bb.yMin, bb.xMax, bb.yMax]]];
    }
    return [[obj.height, obj.width]];
  });
}
